/**
 * BeliefSection.tsx
 * ─────────────────
 * Everything inside the dark-navy section AFTER VillainSection and BEFORE
 * the farmstand (cream bg). Contains:
 *
 *   1. Ingredient polaroids (4 photo cards)
 *   2. Sticky-note brand quote
 *   3. CTA receipt block (bundle photo + itemized pricing + buy button)
 *
 * Must sit inside bg-brand-deepnavy parent — same section as stations + villain.
 */

import React, { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, RotateCw } from "lucide-react";
import { MediaPlaceholder, LpImage } from "@/components/LpMediaPlaceholder";
import { computeReceipt, type LpReceipt } from "@/lib/lp-express-pricing";
import { EditableText, EditableItem, RegenerateButton, EditableElement, LpReorderGroup, LpImageActions, useLpMediaOverride, useLpMediaOverridesMap, useLpV2 } from "./lp-editor";

// Retry affordance (matching the polaroid image footprint) shown when a belief
// card's image generation failed AND a retry handler is available (in-app
// builder only). Clicking regenerates just this one card's image.
function BeliefImageRetry({
  cardIndex,
  onRetry,
}: {
  cardIndex: number;
  onRetry: (cardIndex: number) => void;
}) {
  const [clicked, setClicked] = useState(false);
  return (
    <div
      className="w-full h-full bg-[#EFE9DC] border border-dashed border-[#C7BCA3] flex flex-col items-center justify-center gap-2 px-3 text-center"
      data-testid={`retry-belief-card-${cardIndex}`}
    >
      <span style={{ fontSize: 11, fontWeight: 700, color: "#8A7F68" }}>
        Image failed to generate
      </span>
      <button
        type="button"
        disabled={clicked}
        onClick={() => {
          setClicked(true);
          onRetry(cardIndex);
        }}
        data-testid={`button-retry-belief-card-${cardIndex}`}
        className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-brand-navyaccent)] bg-white px-3 py-1.5 disabled:opacity-60"
        style={{ fontSize: 12, fontWeight: 700, color: "var(--color-brand-navyaccent)" }}
      >
        <RotateCw style={{ width: 13, height: 13 }} className={clicked ? "animate-spin" : ""} />
        {clicked ? "Retrying…" : "Retry"}
      </button>
    </div>
  );
}

// One product-context block per ingredient/belief card. Each is driven by its
// own call to the product-context workflow (image + card_header + card_subtext).
export type BeliefCardContext = {
  cardHeader?: string;
  cardSubtext?: string;
  imageUrl?: string;
  altText?: string;
  imageStatus?: string;
  imageLoading?: boolean;
  hidden?: boolean;
};

const CHECKOUT_URL = "https://thepapabear.com/apps/subscriptions/qc/1sQTsnfo";

const INGREDIENT_CARDS = [
  { img: "/ingredient-tallow-farm.jpg",   name: "Grass-Fed Tallow",  rotate: "-1.5deg", desc: "Shares the same fatty acid profile as your skin. Absorbs in seconds. Doesn't sit on top, doesn't clog, doesn't create dependency." },
  { img: "/ingredient-honey.jpg",          name: "Raw Honey",          rotate: "1.2deg",  desc: "Draws moisture into the skin and holds it there. Naturally antimicrobial. Used for thousands of years. Not invented in a lab." },
  { img: "/ingredient-beeswax-hive.jpg",   name: "Local Beeswax",      rotate: "-0.8deg", desc: "Breathable barrier that seals without suffocating. Sourced within miles of the ranch. Your pores stay clear, your skin stays protected." },
  { img: "/ingredient-oils.jpg",           name: "Cold-Pressed Oils",  rotate: "1.5deg",  desc: "Rosehip, olive, and castor. Each one earns its place. They penetrate, nourish, and repair. Not filler. Not padding. Just work." },
];

// Neutral, brand-agnostic fallbacks used only by Long Form v2 — same shape and
// count as v1 so the layout stays pixel-identical; only the skincare-specific
// imagery paths and copy are generalized.
const INGREDIENT_CARDS_V2 = [
  { img: "Ingredient photo one",   name: "Premium Ingredient", rotate: "-1.5deg", desc: "Chosen for real results. Does exactly what it promises — and nothing it doesn't. No shortcuts." },
  { img: "Ingredient photo two",   name: "Natural Ingredient", rotate: "1.2deg",  desc: "Time-tested and effective. Carefully sourced, never synthetic filler. Used because it works." },
  { img: "Ingredient photo three", name: "Quality Ingredient", rotate: "-0.8deg", desc: "Sourced with care and built to perform. Every component earns its place in the formula." },
  { img: "Ingredient photo four",  name: "Pure Ingredient",    rotate: "1.5deg",  desc: "Each one earns its place. They work together to deliver — not filler, not padding. Just work." },
];

export function BeliefSection({
  receipt,
  bundleImage,
  bundleProductPhotos,
  brandName,
  productContext,
  eyebrow,
  headline,
  headingLoading = false,
  cardsLoading = false,
  declarationQuote,
  declarationAttribution,
  declarationLoading = false,
  checkoutUrl,
  onRetrySlot,
}: {
  // Dynamic receipt model (line items + totals) derived from the actual
  // product(s) + offer + sale. Falls back to an empty receipt for previews.
  receipt?: LpReceipt;
  // Real product flat-lay image; falls back to the dashed placeholder.
  bundleImage?: string | null;
  // Full product photo gallery, backing the "choose from product photos"
  // picker on the CTA receipt image.
  bundleProductPhotos?: string[];
  // Real brand name; falls back to the hardcoded "Papabear" identity literal.
  brandName?: string | null;
  productContext?: BeliefCardContext[];
  eyebrow?: string;
  headline?: string;
  // True while the villain workflow (which supplies the eyebrow/headline) is
  // still generating — show a spinner instead of the static heading.
  headingLoading?: boolean;
  // True while the product-context workflow (which supplies each card's
  // header/subtext) is still generating — show spinners instead of static copy.
  cardsLoading?: boolean;
  // The "declaration" pull-quote + attribution for the sticky note. Falls back
  // to the hardcoded brand quote when not provided.
  declarationQuote?: string;
  declarationAttribution?: string;
  // True while the lp-quote-copy workflow is still generating — show a spinner
  // in the sticky note instead of the hardcoded brand quote.
  declarationLoading?: boolean;
  // Real brand checkout URL. Only consulted in Long Form v2 — v1 keeps the
  // template's hardcoded CHECKOUT_URL so its rendered output stays byte-identical.
  checkoutUrl?: string | null;
  // Retry a single belief-card image by index. In-app builder only; the
  // public/published page leaves it undefined so failed cards stay on the
  // static placeholder (no retry affordance).
  onRetrySlot?: (cardIndex: number) => void;
}) {
  // Heading is driven by the villain workflow (belief_eyebrow / belief_headline);
  // fall back to the static reference copy when not provided.
  const v2 = useLpV2();
  const r: LpReceipt = receipt ?? computeReceipt([], undefined, null);
  // User-uploaded overrides shadow each belief card's AI image, keyed by card
  // index (`productContext:<fi>`). Read once here; index it inside the map.
  const mediaOverrides = useLpMediaOverridesMap();
  const cardOverrideAt = (fi: number): string | undefined => {
    const v = mediaOverrides[`productContext:${fi}`]?.url;
    return typeof v === "string" && v.trim() ? v : undefined;
  };
  // CTA receipt image: a user-uploaded/picked override shadows the real bundle
  // flat-lay; falls back to the dashed placeholder when neither exists.
  const ctaReceiptImage = useLpMediaOverride("product:belief-cta") ?? bundleImage;
  // Ingredient cards: when the product-context workflow has produced entries,
  // render (and allow editing/deleting) those, mirroring the competitor-card
  // pattern; otherwise fall back to the static reference set (read-only preview).
  const ingredientFallback = v2 ? INGREDIENT_CARDS_V2 : INGREDIENT_CARDS;
  const aiCardEntries = (productContext ?? [])
    .map((pc, idx) => ({ pc, idx }))
    .filter(({ pc }) =>
      !!(pc && (pc.cardHeader?.trim() || pc.cardSubtext?.trim() || pc.imageUrl?.trim() || pc.imageLoading || pc.imageStatus)));
  const ingredientCards = aiCardEntries.length
    ? aiCardEntries.map(({ pc, idx }) => ({ f: ingredientFallback[idx % ingredientFallback.length], pc, storedIndex: idx }))
    : ingredientFallback.map((f) => ({ f, pc: undefined as BeliefCardContext | undefined, storedIndex: -1 }));
  return (
    <>
      <LpReorderGroup
        groupKey="belief:blocks"
        items={[
          { key: "belief:ingredients", node: (
      /* ── 1. Ingredient polaroids ── */
      <motion.div
        className="mb-12"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-40px" }}
        variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.1 } } }}
      >
        <div className="mb-8 text-center">
          {headingLoading && !eyebrow?.trim() && !headline?.trim() ? (
            <div
              className="flex items-center justify-center py-3"
              data-testid="loading-belief-heading"
            >
              <Loader2 className="h-6 w-6 animate-spin text-white/70" />
            </div>
          ) : (
            <>
              <EditableText
                as="p"
                path={["villain", "belief_eyebrow"]}
                elementKey="belief:eyebrow"
                value={eyebrow?.trim() || ""}
                placeholder={v2 ? "Built Different" : "Papabear's Built Different"}
                className="text-[var(--color-brand-coral)] text-[10px] font-black uppercase tracking-[0.2em] mb-3"
                testId="text-belief-eyebrow"
              />
              <EditableText
                as="h3"
                regenAnchor
                path={["villain", "belief_headline"]}
                elementKey="belief:headline"
                value={headline?.trim() || ""}
                placeholder={v2 ? "Real ingredients. Nothing artificial." : "Real ingredients. Nothing synthetic."}
                className="text-white font-black text-[22px] leading-tight"
                testId="text-belief-headline"
              />
            </>
          )}
        </div>
        <div className="flex flex-col gap-8 mb-7 px-2">
          {ingredientCards.map(({ f, pc, storedIndex }, ci) => {
            // Drive each card from its own workflow result when present; fall
            // back to the static reference content for previews / no data.
            const editable = storedIndex >= 0;
            const idx = editable ? storedIndex : ci;
            const falUrl = pc?.imageUrl?.trim() || "";
            const overrideUrl = editable ? cardOverrideAt(storedIndex) : undefined;
            const imageUrl = overrideUrl || falUrl;
            const imageAlt = pc?.altText?.trim() || pc?.cardHeader?.trim() || f.name;
            const imageLoading =
              !imageUrl && (cardsLoading || pc?.imageLoading || pc?.imageStatus === "pending");
            // In the in-app builder (onRetrySlot present), an editable card that
            // has no image and isn't loading has effectively failed to generate —
            // show the failure/retry affordance instead of the dev "TODO:" box,
            // regardless of whether the worker tagged it imageStatus === "failed".
            // Static preview cards (no generation attempted) keep the placeholder.
            const imageFailed =
              !imageUrl && !imageLoading && !!onRetrySlot && editable;
            const textLoading =
              cardsLoading && !pc?.cardHeader?.trim() && !pc?.cardSubtext?.trim();
            const cardInner = (
              <div className="w-full bg-white" style={{ padding: "10px 10px 20px 10px", transform: `rotate(${f.rotate})`, boxShadow: "0 8px 32px rgba(0,0,0,0.45), 0 2px 8px rgba(0,0,0,0.3)" }}>
                <div className="relative group/lpimg w-full overflow-hidden" style={{ height: "200px" }}>
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={imageAlt}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      data-testid={`img-belief-card-${idx}`}
                    />
                  ) : imageLoading ? (
                    <div
                      className="w-full h-full bg-[#EFEBE2] animate-pulse flex items-center justify-center"
                      data-testid={`img-belief-card-loading-${idx}`}
                    >
                      <Loader2 className="h-6 w-6 text-[var(--color-brand-deepnavy)]/40 animate-spin" />
                    </div>
                  ) : imageFailed && onRetrySlot ? (
                    <BeliefImageRetry cardIndex={idx} onRetry={onRetrySlot} />
                  ) : (
                    <MediaPlaceholder path={f.img} rounded={false} style={{ height: "100%" }} />
                  )}
                  <LpImageActions
                    slotKey={`productContext:${idx}`}
                    canRegenerate={!!falUrl && !!onRetrySlot}
                    onRegenerate={onRetrySlot ? () => onRetrySlot(idx) : undefined}
                    testIdPrefix={`belief-${idx}`}
                  />
                </div>
                {textLoading ? (
                  <div className="flex items-center justify-center py-4" data-testid={`loading-belief-card-text-${idx}`}>
                    <Loader2 className="h-4 w-4 animate-spin" style={{ color: "var(--color-brand-navy)" }} />
                  </div>
                ) : (
                  <>
                    <EditableText
                      as="p"
                      path={["productContext", idx, "cardHeader"]}
                      value={pc?.cardHeader?.trim() || ""}
                      placeholder={f.name}
                      className="text-center font-serif italic mt-3 mb-1"
                      style={{ color: "var(--color-brand-navy)", fontSize: "15px", letterSpacing: "0.01em" }}
                      testId={`text-belief-card-name-${idx}`}
                      // The productContext copy workflow re-rolls ALL ingredient
                      // cards at once, so surface its inline "Regenerate" control
                      // ONCE — on the first rendered card's header — rather than
                      // on every card. regenAnchor opts in (path[1] is a numeric
                      // index, not a canonical heading key); the block key
                      // auto-derives from the path root ("productContext").
                      regenAnchor={ci === 0}
                      regenBlockKey="productContext"
                    />
                    <EditableText
                      as="p"
                      path={["productContext", idx, "cardSubtext"]}
                      value={pc?.cardSubtext?.trim() || ""}
                      placeholder={f.desc}
                      multiline
                      className="text-center leading-relaxed px-2"
                      style={{ color: "#6B6560", fontSize: "11px" }}
                      testId={`text-belief-card-desc-${idx}`}
                    />
                  </>
                )}
              </div>
            );
            return (
            <motion.div
              key={`${f.name}-${idx}`}
              className="flex flex-col items-center"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.45, delay: ci * 0.08 }}
            >
              {editable ? (
              <EditableItem
                path={["productContext"]}
                index={storedIndex}
                count={productContext?.length ?? 0}
                hidden={pc?.hidden}
                allowDelete
                toolbarSide="left"
                testId={`belief-card-${storedIndex}`}
              >
                {cardInner}
              </EditableItem>
              ) : cardInner}
            </motion.div>
            );
          })}
        </div>
      </motion.div>
          ) },
          { key: "belief:sticky-note-card", node: (
      <EditableElement elementKey="belief:sticky-note-card" testId="element-belief-sticky-note">
      <motion.div
        className="flex justify-center mb-10"
        initial={{ opacity: 0, rotate: -2 }}
        whileInView={{ opacity: 1, rotate: 1.5 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <div style={{ background: "#FFF9C4", padding: "24px 28px 20px", maxWidth: 300, boxShadow: "2px 4px 12px rgba(0,0,0,0.35)", position: "relative" as const }}>
          <div style={{ position: "absolute" as const, top: -6, left: "50%", transform: "translateX(-50%)", width: 60, height: 18, background: "rgba(200,191,179,0.5)", borderRadius: 2 }} />
          {declarationLoading && !declarationQuote?.trim() ? (
            <div className="flex items-center justify-center py-3" data-testid="loading-belief-declaration">
              <Loader2 className="h-5 w-5 animate-spin" style={{ color: "var(--color-brand-deepnavy)", opacity: 0.5 }} />
            </div>
          ) : (
            <>
              <EditableText
                as="p"
                formatOnly
                path={["quotes", "declaration", "quote"]}
                value={declarationQuote?.trim() || ""}
                placeholder={v2 ? "We don't cut corners. We build it right." : "We don't coat your skin. We feed it."}
                multiline
                render={(v) => `"${v || (v2 ? "We don't cut corners. We build it right." : "We don't coat your skin. We feed it.")}"`}
                style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontWeight: 900, fontSize: 20, lineHeight: 1.3, color: "var(--color-brand-deepnavy)", textAlign: "center" as const, margin: 0 }}
                testId="text-belief-declaration-quote"
              />
              <EditableText
                as="p"
                formatOnly
                path={["quotes", "declaration", "attribution"]}
                value={declarationAttribution?.trim() || ""}
                placeholder={brandName?.trim() || (v2 ? "" : "Papabear")}
                style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive", fontSize: 18, color: "#6B6560", textAlign: "right" as const, marginTop: 8 }}
                testId="text-belief-declaration-attribution"
              />
            </>
          )}
        </div>
      </motion.div>
      </EditableElement>
          ) },
          { key: "belief:cta-receipt-card", node: (
      <EditableElement elementKey="belief:cta-receipt-card" toolbarSide="left" testId="element-belief-cta-receipt">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <div style={{ borderRadius: 14, overflow: "hidden", border: "2px solid #D8CFC3", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}>
          <div className="group/lpimg relative">
            <LpImage src={ctaReceiptImage} alt="Product bundle" path={v2 ? "Product bundle photo" : "/bundle-flatlay.jpeg"} rounded={false} objectFit="contain" style={{ height: 240, backgroundColor: "#FAF6EE" }} />
            <LpImageActions slotKey="product:belief-cta" productPhotos={bundleProductPhotos} revertLabel="Use default" revertTitle="Use the default product photo" />
          </div>

          <div style={{ backgroundColor: "var(--color-brand-cream)", padding: "16px 16px 12px" }}>
            {r.lineItems.map((item, i) => (
              <div key={i} className="flex items-center justify-between py-2" style={{ borderBottom: "1px solid #D8CFC3" }}>
                <span className="text-sm" style={{ color: "var(--color-brand-deepnavy)" }}>{item.name}</span>
                {item.priceLabel && (
                  <span className="text-sm font-medium" style={{ color: "#9C9189", textDecoration: r.hasDiscount ? "line-through" : "none" }}>{item.priceLabel}</span>
                )}
              </div>
            ))}
            <div className="flex items-center justify-between py-2" style={{ borderBottom: "1px solid #D8CFC3" }}>
              <span className="text-sm" style={{ color: "var(--color-brand-deepnavy)" }}>U.S. Shipping</span>
              <span className="text-sm font-black" style={{ color: "var(--color-brand-greenaccent)" }}>FREE</span>
            </div>
            {r.hasDiscount && (
              <>
                <div className="flex items-center justify-between pt-3 pb-1">
                  <span className="text-[9px] font-black uppercase tracking-[0.18em]" style={{ color: "#6B6560" }}>Retail Total</span>
                  <span className="text-sm font-semibold" style={{ color: "#9C9189", textDecoration: "line-through" }}>{r.retailLabel}</span>
                </div>
                <div className="flex items-center justify-between pb-3" style={{ borderBottom: "2px solid var(--color-brand-deepnavy)" }}>
                  <span className="text-[9px] font-black uppercase tracking-[0.1em]" style={{ color: "var(--color-brand-coral)" }}>{r.discountRowLabel}{r.discountPercent ? ` (${r.discountPercent}% off)` : ""}</span>
                  <span className="text-sm font-black" style={{ color: "var(--color-brand-coral)" }}>{r.discountAmountLabel}</span>
                </div>
              </>
            )}
            <div className="flex items-center justify-between pt-3" style={!r.hasDiscount ? { borderTop: "2px solid var(--color-brand-deepnavy)", marginTop: 4 } : undefined}>
              <div>
                <p className="font-black text-sm uppercase tracking-wider" style={{ color: "var(--color-brand-deepnavy)" }}>You Pay Today</p>
                <p className="text-[10px]" style={{ color: "#6B6560" }}>{r.guaranteeDays}-day supply{r.perDayLabel ? ` · ${r.perDayLabel}` : ""}</p>
              </div>
              <p className="font-black text-4xl leading-none" style={{ color: "var(--color-brand-navy)" }}>{r.priceTodayLabel}</p>
            </div>
          </div>

          <a href={v2 ? (checkoutUrl || undefined) : CHECKOUT_URL} style={{ display: "block", width: "100%", backgroundColor: "var(--color-brand-greenaccent)", color: "#fff", textAlign: "center" as const, fontWeight: 900, padding: "16px 0" }}>
            <div style={{ fontSize: 18, textTransform: "uppercase" as const, letterSpacing: "0.08em", lineHeight: 1.2 }}>{r.ctaText}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.7)", marginTop: 4 }}>Free shipping · {r.guaranteeDays}-Day money-back guarantee</div>
          </a>

          <div style={{ display: "flex", flexWrap: "wrap" as const, gap: "3px 12px", justifyContent: "center", padding: "10px 16px 14px" }}>
            {r.guaranteeBullets.map((t) => (
              <div key={t} style={{ display: "flex", alignItems: "center", gap: 3 }}>
                <CheckCircle2 style={{ width: 10, height: 10, color: "var(--color-brand-cream)", flexShrink: 0 }} />
                <span style={{ fontSize: 9, fontWeight: 600, color: "rgba(244,237,221,0.7)" }}>{t}</span>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
      </EditableElement>
          ) },
        ]}
      />
    </>
  );
}
